import React, { useState, useEffect, useCallback } from "react";
import { api, useBusiness } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const formatCurrency = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v || 0);

const SEVERITY_STYLES = {
  opportunity: { bg: "bg-[#1e3a5f]/5 border-[#1e3a5f]/20", text: "text-[#1e3a5f]", badge: "bg-[#1e3a5f]/10 text-[#1e3a5f] border-[#1e3a5f]/30" },
  warning: { bg: "bg-amber-50 border-amber-200", text: "text-amber-700", badge: "bg-amber-100 text-amber-700 border-amber-300" },
  info: { bg: "bg-blue-50 border-blue-200", text: "text-blue-700", badge: "bg-blue-100 text-blue-700 border-blue-300" },
};

const TYPE_LABELS = {
  home_office: "Home Office", mileage: "Mileage", meals: "Meals & Entertainment",
  software: "Software", equipment: "Equipment", health_insurance: "Health Insurance",
  retirement: "Retirement", vehicle: "Vehicle", travel: "Travel", contractors: "Contractors"
};

const DeductionFlags = () => {
  const { selectedBusiness } = useBusiness();
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  const fetchFlags = useCallback(async () => {
    if (!selectedBusiness) return;
    setLoading(true);
    try {
      const res = await api.get(`/deduction-flags/${selectedBusiness.id}`);
      setFlags(res.data);
    } catch { toast.error("Failed to load flags"); }
    finally { setLoading(false); }
  }, [selectedBusiness]);

  useEffect(() => { fetchFlags(); }, [fetchFlags]);

  const runScan = async () => {
    setScanning(true);
    try {
      const res = await api.post(`/deduction-flags/scan/${selectedBusiness.id}`);
      setFlags(res.data);
      toast.success(`Scan complete! Found ${res.data.filter(f => f.status === "open").length} opportunities`);
    } catch { toast.error("Scan failed"); }
    finally { setScanning(false); }
  };

  const updateFlag = async (flagId, status) => {
    try {
      await api.put(`/deduction-flags/${flagId}`, { status });
      toast.success(status === "completed" ? "Marked as completed" : "Dismissed");
      fetchFlags();
    } catch { toast.error("Failed to update"); }
  };

  const openFlags = flags.filter(f => f.status === "open");
  const completedFlags = flags.filter(f => f.status === "completed");
  const dismissedFlags = flags.filter(f => f.status === "dismissed");
  const totalSavings = openFlags.reduce((s, f) => s + (f.estimated_savings || 0), 0);

  if (!selectedBusiness) return <div className="flex items-center justify-center min-h-[400px]"><p className="text-muted-foreground">Please select a business.</p></div>;
  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-pulse text-muted-foreground">Loading...</div></div>;

  return (
    <div className="space-y-6" data-testid="deduction-flags-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-stone-900">Deduction Opportunities</h1>
          <p className="text-stone-600">AI-powered scan of your transactions and profile for tax savings</p>
        </div>
        <Button onClick={runScan} disabled={scanning} className="bg-[#1e3a5f] hover:bg-[#2d5a8e]" data-testid="scan-btn">
          {scanning ? (
            <><svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>Scanning...</>
          ) : "Scan for Opportunities"}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-[#1e3a5f]/20 bg-[#1e3a5f]/5"><CardContent className="pt-5 pb-4 text-center">
          <p className="text-2xl font-bold text-[#1e3a5f]">{formatCurrency(totalSavings)}</p>
          <p className="text-xs text-[#1e3a5f] mt-1">Potential Savings</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4 text-center">
          <p className="text-2xl font-bold text-amber-600">{openFlags.length}</p>
          <p className="text-xs text-stone-500 mt-1">Open Items</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4 text-center">
          <p className="text-2xl font-bold text-[#1e3a5f]">{completedFlags.length}</p>
          <p className="text-xs text-stone-500 mt-1">Completed</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4 text-center">
          <p className="text-2xl font-bold text-stone-400">{dismissedFlags.length}</p>
          <p className="text-xs text-stone-500 mt-1">Dismissed</p>
        </CardContent></Card>
      </div>

      {openFlags.length === 0 && !scanning && (
        <Card><CardContent className="pt-8 pb-8 text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center">
            <svg className="w-7 h-7 text-[#1e3a5f]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
          </div>
          <p className="text-stone-700 font-medium">No open opportunities</p>
          <p className="text-sm text-stone-500 mt-1">Run a scan to check for new deduction opportunities</p>
        </CardContent></Card>
      )}

      {openFlags.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-stone-800">Open Opportunities</h2>
          {openFlags.map(flag => {
            const style = SEVERITY_STYLES[flag.severity] || SEVERITY_STYLES.info;
            return (
              <Card key={flag.id} className={style.bg} data-testid={`flag-${flag.id}`}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`font-semibold text-sm ${style.text}`}>{flag.title}</span>
                        <Badge variant="outline" className={`text-[10px] ${style.badge}`}>{TYPE_LABELS[flag.type] || flag.type}</Badge>
                        <Badge variant="outline" className={`text-[10px] ${style.badge}`}>{flag.severity}</Badge>
                      </div>
                      <p className="text-sm text-stone-600">{flag.description}</p>
                      {flag.estimated_savings && (
                        <p className="text-sm font-semibold text-[#1e3a5f] mt-1">Estimated savings: {formatCurrency(flag.estimated_savings)}</p>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" onClick={() => updateFlag(flag.id, "completed")} className="bg-[#1e3a5f] hover:bg-[#2d5a8e]" data-testid={`complete-flag-${flag.id}`}>Done</Button>
                      <Button size="sm" variant="outline" onClick={() => updateFlag(flag.id, "dismissed")} data-testid={`dismiss-flag-${flag.id}`}>Dismiss</Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {completedFlags.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-stone-800">Completed</h2>
          {completedFlags.map(flag => (
            <Card key={flag.id} className="opacity-60" data-testid={`flag-completed-${flag.id}`}>
              <CardContent className="pt-3 pb-3">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-[#1e3a5f] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  <span className="text-sm text-stone-700">{flag.title}</span>
                  {flag.estimated_savings && <span className="text-xs text-[#1e3a5f] font-medium">{formatCurrency(flag.estimated_savings)}</span>}
                  <Badge variant="outline" className="text-[10px]">{TYPE_LABELS[flag.type] || flag.type}</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default DeductionFlags;
